// Matematik araçları: hazır işaretler ile formül, sayı doğrusu ve koordinat düzlemi yerleşimi.
// Yerleşim bir kez, çizimi oluşturan istemcide hesaplanır ve sonuç (yazılar + çizgiler) saklanır;
// böylece her ekran ve PDF çıktısı birebir aynı çizer. Yazı genişliği dışarıdan verilir: tarayıcıda
// canvas ölçer, testlerde sabit bir ölçü kullanılır.
import type { FigureLine, FigureName, FigureText } from './protocol';

export type Measure = (text: string, size: number) => number;

export interface Figure {
  name: FigureName;
  w: number;
  h: number;
  texts: FigureText[];
  lines: FigureLine[];
}

export class MathInputError extends Error {}

export const SYMBOL_GROUPS: { label: string; symbols: string[] }[] = [
  { label: 'İşlem', symbols: ['+', '−', '×', '÷', '±', '∓', '·', '%', '∘', '∝'] },
  { label: 'Karşılaştırma', symbols: ['=', '≠', '≈', '≡', '<', '>', '≤', '≥'] },
  { label: 'Analiz', symbols: ['∑', '∏', '∫', '∬', '∮', '∂', '∇', '∆', '√', '∛', '∞', '′', '″'] },
  { label: 'Yunan harfleri', symbols: ['π', 'θ', 'α', 'β', 'γ', 'δ', 'ε', 'λ', 'μ', 'σ', 'φ', 'ω', 'Δ', 'Σ', 'Ω'] },
  { label: 'Geometri', symbols: ['°', '∠', '△', '⊥', '∥'] },
  { label: 'Küme ve mantık', symbols: ['∈', '∉', '⊂', '⊆', '∪', '∩', '∅', 'ℝ', 'ℕ', 'ℤ', 'ℚ', '∀', '∃', '¬', '∧', '∨', '⇒', '⇔', '→', '←', '↔', '∴', '∵'] },
  { label: 'Üs, indis ve kesir', symbols: ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹', '⁺', '⁻', 'ⁿ', '₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉', 'ᵢ', 'ₙ', '½', '⅓', '¼', '¾'] },
];

const ASCENT = 0.78;
const DESCENT = 0.22;
const r2 = (n: number) => Math.round(n * 100) / 100;

/** Kutuyu serbestçe kurar, sonunda her şeyi sol üst köşesi (0,0) olacak şekilde kaydırır. */
class Canvas {
  readonly texts: FigureText[] = [];
  readonly lines: FigureLine[] = [];
  private minX = Infinity;
  private minY = Infinity;
  private maxX = -Infinity;
  private maxY = -Infinity;

  constructor(private readonly measure: Measure) {}

  private grow(x1: number, y1: number, x2: number, y2: number) {
    this.minX = Math.min(this.minX, x1);
    this.minY = Math.min(this.minY, y1);
    this.maxX = Math.max(this.maxX, x2);
    this.maxY = Math.max(this.maxY, y2);
  }

  width(text: string, size: number) {
    return text ? this.measure(text, size) : 0;
  }

  /** `y` taban çizgisidir; yazı genişliğini döndürür. */
  text(t: string, x: number, y: number, s: number): number {
    if (!t) return 0;
    const w = this.width(t, s);
    this.texts.push({ t, x, y, s });
    this.grow(x, y - ASCENT * s, x + w, y + DESCENT * s);
    return w;
  }

  centered(t: string, cx: number, y: number, s: number) {
    this.text(t, cx - this.width(t, s) / 2, y, s);
  }

  line(x1: number, y1: number, x2: number, y2: number, w: number) {
    this.lines.push([x1, y1, x2, y2, w]);
    this.grow(Math.min(x1, x2) - w / 2, Math.min(y1, y2) - w / 2, Math.max(x1, x2) + w / 2, Math.max(y1, y2) + w / 2);
  }

  finish(name: FigureName): Figure {
    const dx = -this.minX;
    const dy = -this.minY;
    return {
      name,
      w: r2(this.maxX - this.minX),
      h: r2(this.maxY - this.minY),
      texts: this.texts.map((t) => ({ t: t.t, x: r2(t.x + dx), y: r2(t.y + dy), s: r2(t.s) })),
      lines: this.lines.map(([x1, y1, x2, y2, w]) => [r2(x1 + dx), r2(y1 + dy), r2(x2 + dx), r2(y2 + dy), r2(w)]),
    };
  }
}

export type FormulaName = 'integral' | 'sum' | 'product' | 'fraction' | 'root' | 'power' | 'limit';

export interface FormulaField {
  key: string;
  label: string;
  initial: string;
}

export const FORMULAS: Record<FormulaName, { label: string; fields: FormulaField[] }> = {
  integral: {
    label: 'İntegral',
    fields: [
      { key: 'upper', label: 'Üst sınır (boş olabilir)', initial: 'b' },
      { key: 'lower', label: 'Alt sınır (boş olabilir)', initial: 'a' },
      { key: 'body', label: 'İfade', initial: 'f(x) dx' },
    ],
  },
  sum: {
    label: 'Toplam',
    fields: [
      { key: 'upper', label: 'Üst sınır', initial: 'n' },
      { key: 'lower', label: 'Alt sınır', initial: 'k = 1' },
      { key: 'body', label: 'İfade', initial: 'k²' },
    ],
  },
  product: {
    label: 'Çarpım',
    fields: [
      { key: 'upper', label: 'Üst sınır', initial: 'n' },
      { key: 'lower', label: 'Alt sınır', initial: 'k = 1' },
      { key: 'body', label: 'İfade', initial: 'k' },
    ],
  },
  fraction: {
    label: 'Kesir',
    fields: [
      { key: 'top', label: 'Pay', initial: 'x + 1' },
      { key: 'bottom', label: 'Payda', initial: 'x − 1' },
    ],
  },
  root: {
    label: 'Kök',
    fields: [
      { key: 'index', label: 'Kök derecesi (boş: karekök)', initial: '' },
      { key: 'body', label: 'Kök içi', initial: 'x² + 1' },
    ],
  },
  power: {
    label: 'Üs ve indis',
    fields: [
      { key: 'base', label: 'Taban', initial: 'x' },
      { key: 'sup', label: 'Üs (boş olabilir)', initial: 'n' },
      { key: 'sub', label: 'İndis (boş olabilir)', initial: '' },
    ],
  },
  limit: {
    label: 'Limit',
    fields: [
      { key: 'under', label: 'Yaklaşım', initial: 'x → 0' },
      { key: 'body', label: 'İfade', initial: 'f(x)' },
    ],
  },
};

/** Taban çizgisi y = 0; büyük işaretler matematik eksenine (taban çizgisinin biraz üstü) ortalanır. */
export function layoutFormula(name: FormulaName, v: Record<string, string>, s: number, weight: number, measure: Measure): Figure {
  const c = new Canvas(measure);
  const small = s * 0.62;
  const axis = -0.3 * s;
  const get = (k: string) => (v[k] ?? '').trim();

  switch (name) {
    case 'integral': {
      const big = s * 2;
      const gw = c.text('∫', 0, axis + 0.3 * big, big);
      const upperW = c.text(get('upper'), gw + 0.05 * s, axis - 0.72 * big + ASCENT * small, small);
      const lowerW = c.text(get('lower'), gw - 0.22 * s, axis + 0.72 * big, small);
      c.text(get('body'), Math.max(gw + 0.15 * s, gw + 0.05 * s + upperW + 0.2 * s, gw - 0.22 * s + lowerW + 0.2 * s), 0, s);
      break;
    }
    case 'sum':
    case 'product': {
      const glyph = name === 'sum' ? '∑' : '∏';
      const big = s * 1.7;
      const upper = get('upper');
      const lower = get('lower');
      const gw = c.width(glyph, big);
      const col = Math.max(gw, c.width(upper, small), c.width(lower, small));
      const cx = col / 2;
      const glyphBase = axis + 0.37 * big;
      c.centered(glyph, cx, glyphBase, big);
      c.centered(upper, cx, glyphBase - 0.78 * big - 0.12 * s, small);
      c.centered(lower, cx, glyphBase + 0.14 * big + ASCENT * small + 0.08 * s, small);
      c.text(get('body'), col + 0.3 * s, 0, s);
      break;
    }
    case 'fraction': {
      const top = get('top');
      const bottom = get('bottom');
      const w = Math.max(c.width(top, s), c.width(bottom, s), s) + 0.3 * s;
      c.line(0, axis, w, axis, weight);
      c.centered(top, w / 2, axis - 0.22 * s - DESCENT * s, s);
      c.centered(bottom, w / 2, axis + 0.22 * s + ASCENT * s, s);
      break;
    }
    case 'root': {
      const index = get('index');
      const body = get('body');
      const bw = Math.max(c.width(body, s), s);
      const top = -(ASCENT + 0.14) * s;
      const bottom = DESCENT * s;
      const left = index ? Math.max(c.width(index, small * 0.85) - 0.1 * s, 0) : 0;
      const start = left + 0.5 * s;
      if (index) c.text(index, 0, -0.42 * s, small * 0.85);
      c.line(left, 0.02 * s, left + 0.14 * s, -0.08 * s, weight);
      c.line(left + 0.14 * s, -0.08 * s, left + 0.27 * s, bottom, weight * 1.5);
      c.line(left + 0.27 * s, bottom, start, top, weight);
      c.line(start, top, start + bw + 0.2 * s, top, weight);
      c.text(body, start + 0.1 * s, 0, s);
      break;
    }
    case 'power': {
      const w = c.text(get('base') || 'x', 0, 0, s);
      c.text(get('sup'), w + 0.04 * s, -0.42 * s, small);
      c.text(get('sub'), w + 0.04 * s, 0.2 * s, small);
      break;
    }
    case 'limit': {
      const under = get('under');
      const limW = c.width('lim', s);
      const col = Math.max(limW, c.width(under, small));
      c.centered('lim', col / 2, 0, s);
      c.centered(under, col / 2, DESCENT * s + ASCENT * small, small);
      c.text(get('body'), col + 0.3 * s, 0, s);
      break;
    }
  }
  return c.finish(name);
}

function formatNumber(n: number): string {
  const rounded = Math.round(n * 10000) / 10000 || 0;
  return String(Math.abs(rounded)).replace('.', ',').replace(/^/, rounded < 0 ? '−' : '');
}

function arrowHead(c: Canvas, x: number, y: number, dx: number, dy: number, size: number, weight: number) {
  c.line(x, y, x - dx * size - dy * size * 0.5, y - dy * size - dx * size * 0.5, weight);
  c.line(x, y, x - dx * size + dy * size * 0.5, y - dy * size + dx * size * 0.5, weight);
}

function steps(min: number, max: number, step: number, limit: number, what: string): number[] {
  if (![min, max, step].every(Number.isFinite)) throw new MathInputError('Lütfen sayıları kontrol edin.');
  if (min >= max) throw new MathInputError(`${what}: en küçük değer en büyükten küçük olmalı.`);
  if (step <= 0) throw new MathInputError(`${what}: adım sıfırdan büyük olmalı.`);
  const count = Math.floor((max - min) / step + 1e-9);
  if (count > limit) throw new MathInputError(`${what}: en fazla ${limit} aralık olabilir, adımı büyütün.`);
  if (count < 1) throw new MathInputError(`${what}: adım aralıktan büyük olamaz.`);
  return Array.from({ length: count + 1 }, (_, i) => min + i * step);
}

export function layoutNumberLine(opts: { min: number; max: number; step: number; length: number }, s: number, weight: number, measure: Measure): Figure {
  const values = steps(opts.min, opts.max, opts.step, 40, 'Sayı doğrusu');
  const c = new Canvas(measure);
  const label = s * 0.7;
  const pad = s * 0.9;
  const head = s * 0.45;
  const len = opts.length;
  const at = (v: number) => ((v - opts.min) / (opts.max - opts.min)) * len;
  c.line(-pad, 0, len + pad, 0, weight);
  arrowHead(c, -pad, 0, -1, 0, head, weight);
  arrowHead(c, len + pad, 0, 1, 0, head, weight);

  const texts = values.map(formatNumber);
  const widest = Math.max(...texts.map((t) => c.width(t, label)));
  const spacing = values.length > 1 ? at(values[1]) - at(values[0]) : len;
  const stride = Math.max(1, Math.ceil((widest + label * 0.5) / spacing));
  const zero = values.findIndex((v) => Math.abs(v) < 1e-9);
  const offset = zero >= 0 ? zero % stride : 0; // etiketler seyrekse sıfır yine görünsün
  values.forEach((v, i) => {
    const labelled = (i - offset) % stride === 0;
    const tick = labelled ? s * 0.32 : s * 0.2;
    c.line(at(v), -tick, at(v), tick, weight);
    if (labelled) c.centered(texts[i], at(v), s * 0.32 + ASCENT * label + s * 0.1, label);
  });
  return c.finish('numberLine');
}

export function layoutCoordinatePlane(opts: { xMin: number; xMax: number; yMin: number; yMax: number; cell: number }, s: number, weight: number, measure: Measure): Figure {
  const xs = steps(opts.xMin, opts.xMax, 1, 30, 'Yatay eksen');
  const ys = steps(opts.yMin, opts.yMax, 1, 30, 'Dikey eksen');
  const c = new Canvas(measure);
  const cell = opts.cell;
  const label = s * 0.55;
  const X = (v: number) => (v - opts.xMin) * cell;
  const Y = (v: number) => (opts.yMax - v) * cell;
  const ox = opts.xMin <= 0 && opts.xMax >= 0 ? X(0) : 0;
  const oy = opts.yMin <= 0 && opts.yMax >= 0 ? Y(0) : Y(opts.yMin);
  const w = X(opts.xMax);
  const h = Y(opts.yMin);
  const grid = Math.min(0.4, weight * 0.25);
  const over = s * 0.8;
  const head = s * 0.4;
  const tick = s * 0.14;

  for (const v of xs) c.line(X(v), 0, X(v), h, grid);
  for (const v of ys) c.line(0, Y(v), w, Y(v), grid);
  c.line(-over * 0.3, oy, w + over, oy, weight);
  c.line(ox, h + over * 0.3, ox, -over, weight);
  arrowHead(c, w + over, oy, 1, 0, head, weight);
  arrowHead(c, ox, -over, 0, -1, head, weight);
  c.text('x', w + over * 0.4, oy + s * 0.2 + ASCENT * label, label * 1.15);
  c.text('y', ox + s * 0.28, -over + ASCENT * label * 0.6, label * 1.15);

  const strideFor = (texts: string[], spacing: number) => Math.max(1, Math.ceil((Math.max(...texts.map((t) => c.width(t, label))) + label * 0.4) / spacing));
  const strideX = strideFor(xs.map(formatNumber), cell);
  const strideY = Math.max(1, Math.ceil((label * 1.1) / cell));
  for (const v of xs) {
    if (Math.abs(v) < 1e-9 || Math.round(v) % strideX !== 0) continue;
    c.line(X(v), oy - tick, X(v), oy + tick, weight);
    c.centered(formatNumber(v), X(v), oy + tick + ASCENT * label + s * 0.06, label);
  }
  for (const v of ys) {
    if (Math.abs(v) < 1e-9 || Math.round(v) % strideY !== 0) continue;
    c.line(ox - tick, Y(v), ox + tick, Y(v), weight);
    const t = formatNumber(v);
    c.text(t, ox - tick - s * 0.08 - c.width(t, label), Y(v) + (ASCENT - DESCENT) * label * 0.5, label);
  }
  if (opts.xMin <= 0 && opts.xMax >= 0 && opts.yMin <= 0 && opts.yMax >= 0) {
    c.text('0', ox - s * 0.08 - c.width('0', label), oy + ASCENT * label + s * 0.06, label);
  }
  return c.finish('coordinatePlane');
}
